import { Button, Card, LinkButton, LogoStamp } from '@croissants/ui';
import type { JSX } from 'react';
import { Loading } from '../components/feedback';
import { contactLink } from '../lib/config';
import { useLocale } from '../state/locale';
import { useSession } from '../state/session';

/**
 * The screens the storefront can open onto before it opens onto a storefront
 * (PRD GST-001, GST-040).
 *
 * None of them is an error page. A guest who scanned a code that does not resolve
 * is standing in a flat holding a phone, and "500" helps them not at all - so each
 * case says what happened and what to do next.
 */

export function ScanPrompt(): JSX.Element {
  const { t } = useLocale();
  return (
    <div className="gw-hero">
      <LogoStamp size={112} />
      <Card title={t('landing.noToken.title')}>
        <p>{t('landing.noToken.body')}</p>
      </Card>
    </div>
  );
}

/**
 * The copy promises two ways out - "ask your host for the current one, or
 * contact us" - so the screen must OFFER both (GST-040: never a dead end). The
 * sibling not-servable screen carries the same contact route (WEB-008); this
 * one rendered the sentence with no link at all, which made "contactez-nous" a
 * promise the screen did not keep. As everywhere, the link is offered only when
 * a public site is configured - a dead link is worse than none.
 */
export function InvalidCode(): JSX.Element {
  const { locale, t } = useLocale();
  const { forget } = useSession();
  const contact = contactLink(locale);
  return (
    <div className="gw-hero">
      <LogoStamp size={96} />
      <Card title={t('landing.invalid.title')}>
        <p>{t('landing.invalid.body')}</p>
        <div className="gw-item__actions">
          <Button variant="secondary" onClick={forget}>
            {t('error.rescan')}
          </Button>
          {contact === null ? null : (
            <LinkButton variant="ghost" href={contact} target="_blank" rel="noreferrer">
              {t('landing.invalid.contactAction')}
            </LinkButton>
          )}
        </div>
      </Card>
    </div>
  );
}

/**
 * The resolve is unsettled but nothing is in flight - the connection dropped, or
 * the retry is paused because the phone reports itself offline.
 *
 * This screen exists because the alternative is an unattended spinner: the query
 * is still "pending", no further request will be sent, and the guest waits on a
 * promise nobody is keeping. Saying so and offering the retry is the honest
 * version, and it is a different sentence from "this code did not open" - the
 * code is very probably fine.
 */
export function Unreachable(): JSX.Element {
  const { t, pt } = useLocale();
  const { retry, forget } = useSession();
  return (
    <div className="gw-hero">
      <LogoStamp size={96} />
      <Card title={t('landing.unreachable.title')}>
        <p>{pt('error.network')}</p>
        <div className="gw-item__actions">
          <Button onClick={retry}>{pt('action.retry')}</Button>
          <Button variant="ghost" onClick={forget}>
            {t('error.rescan')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * PRD GST-040: "a defined 'not yet available here' screen with honest copy -
 * never a dead-end error." The same treatment covers a paused service (CON-008,
 * OPS-009), which is a different sentence and the same promise.
 *
 * "Never a dead-end" is the operative half, and it is about controls, not only
 * about copy: a screen whose last line promises the guest can tell us where they
 * are, and which then renders nothing to do it with, is the dead end the
 * requirement exists to prevent. So both branches carry a way onward. Serviceable
 * addresses are not something this screen can capture - the demand-signal capture
 * of WEB-004 is deferred - so it does not pretend to; it routes to the contact
 * channel the public site publishes (WEB-008) when one is configured, and always
 * offers the guest the one thing that works from here, dropping this code and
 * scanning another. Ordering is only paused for a while, so that branch offers
 * the retry instead.
 */
export function Unavailable({
  reason,
}: {
  reason: 'NOT_SERVABLE' | 'SERVICE_PAUSED';
}): JSX.Element {
  const { locale, t, pt } = useLocale();
  const { forget, retry } = useSession();
  const notServable = reason === 'NOT_SERVABLE';
  const title = notServable ? 'storefront.notServable.title' : 'storefront.servicePaused.title';
  const body = notServable ? 'storefront.notServable.body' : 'storefront.servicePaused.body';
  const contact = contactLink(locale);
  return (
    <div className="gw-hero">
      <LogoStamp size={96} />
      <Card title={pt(title)}>
        <p>{pt(body)}</p>
        {notServable ? <p className="gw-muted">{t('unavailable.contact')}</p> : null}
        <div className="gw-item__actions">
          {notServable ? null : <Button onClick={retry}>{pt('action.retry')}</Button>}
          {contact === null ? null : (
            <LinkButton variant="secondary" href={contact} target="_blank" rel="noreferrer">
              {t('unavailable.contactAction')}
            </LinkButton>
          )}
          <Button variant="ghost" onClick={forget}>
            {t('unavailable.rescan')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Neither pre-order nor immediate delivery is switched on (PRD OPS-025,
 * GST-042).
 *
 * The same frame as the paused screen and deliberately not the same sentence: a
 * pause is temporary and its copy promises "a little later", which is a promise
 * nobody has made here. This says what is true - ordering is closed for now -
 * and offers the same three ways onward, the retry included, because the switch
 * is one tick away from being back on.
 *
 * There is no fourth control naming "your orders". The tab bar is still drawn
 * behind this screen and still leads there, and a button restating a tab the
 * guest can already see is the unrequested copy this app keeps removing.
 */
export function OrderingClosed(): JSX.Element {
  const { locale, t, pt } = useLocale();
  const { forget, retry } = useSession();
  const contact = contactLink(locale);
  return (
    <div className="gw-hero">
      <LogoStamp size={96} />
      <Card title={t('storefront.closed.title')}>
        <div className="gw-item__actions">
          <Button onClick={retry}>{pt('action.retry')}</Button>
          {contact === null ? null : (
            <LinkButton variant="secondary" href={contact} target="_blank" rel="noreferrer">
              {t('unavailable.contactAction')}
            </LinkButton>
          )}
          <Button variant="ghost" onClick={forget}>
            {t('unavailable.rescan')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function ResolvingSession(): JSX.Element {
  const { t } = useLocale();
  return <Loading label={t('landing.resolving')} />;
}
